import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2,
  Users,
  Target,
  TrendingUp,
  CheckCircle,
  Star,
  FileText,
  Search,
  Filter,
  Plus,
  ArrowRight,
  Clock,
  DollarSign,
  Globe,
  Zap,
  Shield,
  Award,
  BarChart3,
  MessageSquare,
  Calendar,
  MapPin,
  Briefcase,
  Mail,
  Phone,
  Play,
  Download,
  Lightbulb,
  Cpu,
  Database,
  Cloud,
  Lock,
  Settings,
  PieChart,
  GraduationCap,
} from "lucide-react";
import CommonContactUs from "./CommonContactUs";

const ForCompaniesPage = () => {
  const [activeTab, setActiveTab] = useState("solutions");

  const solutions = [
    {
      icon: <Briefcase className="w-8 h-8 text-blue-600" />,
      title: "Contract Staffing",
      description:
        "Flexible staffing solutions connecting you with project-based opportunities and temporary workforce requirements.",
      features: [
        "High-volume hiring",
        "Multi-location support",
        "Custom workflows",
        "Advanced analytics",
      ],
      link: "/contract-staffing",
    },
    {
      icon: <Search className="w-8 h-8 text-green-600" />,
      title: "Job Matching",
      description:
        "Connecting skilled contractors with opportunities that match their expertise and preferred engagement models.",
      features: [
        "HRIS integration",
        "Automated workflows",
        "Performance management",
        "Employee self-service",
      ],
      link: "/job-matching",
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#1A193B] pt-20 text-white">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-3 lg:px-4 py-24 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              For Contractors
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-indigo-100">
              Flexible engagement models for independent professionals_
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {/* Enterprise Solutions */}
              Services for Contractors
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              TechMind offers specialized services designed for independent
              contractors and freelance professionals in the tech industry. We
              help you find rewarding contract opportunities that match your
              skills and preferred working style.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                className="bg-white rounded-lg shadow-sm border p-8 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center mb-6">
                  {solution.icon}
                  <h3 className="text-2xl font-semibold text-gray-900 ml-4">
                    {solution.title}
                  </h3>
                </div>
                <p className="text-gray-600 mb-6 text-lg">
                  {solution.description}
                </p>
                {/* <ul className="space-y-3 mb-6">
                  {solution.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-600">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul> */}
                <Link
                  to={solution.link}
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-lg"
                >
                  Learn More
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      {/* <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Professional Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert services to support your implementation and ongoing success
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
                className="bg-gray-50 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-center mb-4">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4 text-center">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Process Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Benefits for Contractors
            </h2>
            {/* <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A structured approach to ensure successful implementation and adoption
            </p> */}
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Access to Premium Opportunities",
                description:
                  "Gain access to high-quality contract opportunities with leading companies that may not be publicly advertised.",
              },
              {
                step: "02",
                title: "Flexible Engagement Models",
                description:
                  "Choose from various engagement models that suit your working style and preferences, from short-term projects to long-term contracts.",
              },
              {
                step: "03",
                title: "Competitive Rates",
                description:
                  "We help negotiate competitive rates that reflect your skills and experience, ensuring you're fairly compensated for your expertise.",
              },
              {
                step: "04",
                title: "Administrative Support",
                description:
                  "We handle many of the administrative aspects of contracting, allowing you to focus on what you do best.",
              },
            ].map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.9 + index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-indigo-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {process.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {process.title}
                </h3>
                <p className="text-gray-600 text-sm">{process.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="flex justify-center gap-4 flex-wrap mb-8"
      >
        <Link to="/services">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-gray-100 text-gray-800 hover:bg-gray-200 px-6 py-3 rounded-lg font-medium transition-colors shadow-md inline-flex items-center"
          >
            <span>Back to Services</span>
          </motion.button>
        </Link>
        <Link to="/contact">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg shadow-lg flex items-center space-x-2 transition-colors duration-300"
          >
            <GraduationCap size={18} />
            <span>Contact Us</span>
          </motion.button>
        </Link>
      </motion.div>


      <section className="py-16 bg-[#1A193B] text-white">
        <CommonContactUs/>
      </section>
    </div>
  );
};

export default ForCompaniesPage;
